import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  Box,
  IconButton,
  FormControl,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import axios from "axios";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";

const AssignModal = ({ open, onClose, cUserDataArr }) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedUserData, setSelectedUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const { handleToastOpen } = useToast();

  const modalStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const paperStyle = {
    backgroundColor: "#ffffff",
    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.064)",
    padding: "20px",
    textAlign: "center",
    borderRadius: "5px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    position: "relative",
  };

  // Filter users based on search term
  const filteredUsers = users.filter((user) =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Fetch users data when modal opens
  const fetchUsers = async () => {
    try {
      setFetchingUsers(true);
      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${process.env.REACT_APP_BE_URL}/asset_it/conas-users`,
        {
          headers: {
            "x-api-key": token,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && response.data.data) {
        setUsers(response.data.data);

        // Auto-select user if name matches from cUserDataArr
        if (cUserDataArr?.name) {
          const matchingUser = response.data.data.find(
            (user) =>
              user.name &&
              user.name.toLowerCase() === cUserDataArr.name.toLowerCase()
          );

          if (matchingUser) {
            setSelectedUser(matchingUser.name);
            setSelectedUserData(matchingUser);
            setSearchTerm(matchingUser.name);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      handleToastOpen("error", "Failed to fetch users data");
    } finally {
      setFetchingUsers(false);
    }
  };

  // Initialize when modal opens
  useEffect(() => {
    if (open) {
      fetchUsers();
    } else {
      // Reset state when modal closes
      setSelectedUser("");
      setSelectedUserData(null);
      setUsers([]);
      setFetchingUsers(false);
      setSearchTerm("");
      setDropdownOpen(false);
    }
  }, [open]);

  // Handle user selection from dropdown
  const handleUserChange = (event) => {
    const selectedUserName = event.target.value;
    setSelectedUser(selectedUserName);
    setSearchTerm(selectedUserName);

    // Find the selected user data
    const userData = users.find((user) => user.name === selectedUserName);
    setSelectedUserData(userData);
    setDropdownOpen(false);
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    
    // If search term matches exactly with a user name, select it
    const exactMatch = users.find(
      (user) => user.name?.toLowerCase() === value.toLowerCase()
    );
    
    if (exactMatch) {
      setSelectedUser(exactMatch.name);
      setSelectedUserData(exactMatch);
    } else if (selectedUser && !value) {
      // If search is cleared, also clear selection
      setSelectedUser("");
      setSelectedUserData(null);
    } else if (selectedUser && !users.find(user => user.name === selectedUser)?.name?.toLowerCase().includes(value.toLowerCase())) {
      // If current selection doesn't match search, clear selection
      setSelectedUser("");
      setSelectedUserData(null);
    }
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchTerm("");
    setSelectedUser("");
    setSelectedUserData(null);
  };

  // Handle dropdown focus
  const handleDropdownFocus = () => {
    setDropdownOpen(true);
  };

  // Handle dropdown blur
  const handleDropdownBlur = () => {
    // Small delay to allow menu item click to register
    setTimeout(() => {
      setDropdownOpen(false);
    }, 150);
  };

  // Validate and save assignment
  const onSave = async () => {
    // Validation
    if (!selectedUser) {
      handleToastOpen("error", "Please select a user name");
      return;
    }

    if (!selectedUserData) {
      handleToastOpen("error", "Selected user data not found");
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const requestBody = {
        asset_id: cUserDataArr.asset_id,
        user_name: selectedUserData.name,
        emp_code: selectedUserData.emp_code,
        // Add any other required fields
      };

      const response = await axios.put(
        `${process.env.REACT_APP_BE_URL}/asset_it/assign-asset`,
        requestBody,
        {
          headers: {
            "x-api-key": token,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        handleToastOpen("success", "Asset assigned successfully");
        onClose();
        // Reload page or update parent component
        window.location.reload();
      }
    } catch (error) {
      console.error("Error assigning asset:", error);
      handleToastOpen(
        "error",
        error.response?.data?.message || "Failed to assign asset"
      );
    } finally {
      setLoading(false);
    }
  };

  // Get employee code status message
  const getEmployeeCodeStatus = () => {
    if (!selectedUserData) return null;

    const hasEmpCode =
      selectedUserData.emp_code &&
      selectedUserData.emp_code !== null &&
      selectedUserData.emp_code !== "" &&
      selectedUserData.emp_code !== "null";

    return (
      <Typography
        variant="body2"
        sx={{
          mt: '0.5em',
          mb: '1.5em',
          color: hasEmpCode ? "green" : "orange",
          fontWeight: "500",
          textAlign: "left"
        }}
      >
        {hasEmpCode
          ? `Employee code: ${selectedUserData.emp_code}`
          : "Employee code is not present"}
      </Typography>
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="assign-modal-title"
      aria-describedby="assign-modal-description"
      style={modalStyle}
    >
      <Box sx={{ ...paperStyle, width: "400px" }}>
        {/* Close Icon */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "#555",
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          variant="h6"
          sx={{ textAlign: "start", marginBottom: "1.5em" }}
        >
          Assign Asset
        </Typography>

        {/* Searchable User Dropdown */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <TextField
            label="Select User"
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={handleDropdownFocus}
            onBlur={handleDropdownBlur}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            required
            disabled={loading || fetchingUsers}
            placeholder="Type to search users..."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {fetchingUsers ? (
                    <CircularProgress size={16} />
                  ) : searchTerm && (
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      sx={{ padding: 0 }}
                    >
                      <ClearIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingRight: '14px',
              },
            }}
          />

          {/* Dropdown Menu */}
          {dropdownOpen && !fetchingUsers && (
            <Box
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                zIndex: 1300,
                maxHeight: '200px',
                overflowY: 'auto',
                mt: '4px',
              }}
            >
              {filteredUsers.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center', color: '#666' }}>
                  {searchTerm ? `No users found for "${searchTerm}"` : 'No users available'}
                </Box>
              ) : (
                filteredUsers.map((user) => (
                  <Box
                    key={user.id || user.name}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setSelectedUser(user.name);
                      setSelectedUserData(user);
                      setSearchTerm(user.name);
                      setDropdownOpen(false);
                    }}
                    sx={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      borderBottom: '1px solid #f0f0f0',
                      backgroundColor: selectedUser === user.name ? '#f5f5f5' : 'transparent',
                      '&:hover': {
                        backgroundColor: '#f0f0f0',
                      },
                      '&:last-child': {
                        borderBottom: 'none',
                      },
                      textAlign:'left'
                    }}
                  >
                    {user.name}
                  </Box>
                ))
              )}
            </Box>
          )}
        </FormControl>

        {/* Employee Code Status - Always rendered when user is selected */}
        {selectedUserData && getEmployeeCodeStatus()}

        {/* Action Buttons - Always visible */}
        <Box sx={{ mt: "auto", pt: 2 }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onSave}
            sx={{ ml: 2 }}
            disabled={loading || fetchingUsers}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : "Save"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AssignModal;